/**
 * HTTP status code enum.
 *
 * Contains only the status codes used by Hungryroot applications.
 */
export enum StatusCode {
  /**
   * Standard response for successful HTTP requests. The actual response will
   * depend on the request method used. In a GET request, the response will
   * contain an entity corresponding to the requested resource. In a POST
   * request, the response will contain an entity describing or containing the
   * result of the action.
   */
  SuccessOK = 200,

  /**
   * The request has been fulfilled, resulting in the creation of a new
   * resource.
   */
  SuccessCreated = 201,

  /**
   * The request has been accepted for processing, but the processing has not
   * been completed. The request might or might not be eventually acted upon,
   * and may be disallowed when processing occurs.
   */
  SuccessAccepted = 202,

  /**
   * The server successfully processed the request and is not returning any
   * content.
   */
  SuccessNoContent = 204,

  /**
   * Indicates multiple options for the resource from which the client may
   * choose (via agent-driven content negotiation). Used as an upper bound for
   * success status checks.
   */
  RedirectMultipleChoices = 300,

  /**
   * The server cannot or will not process the request due to an apparent
   * client error (e.g., malformed request syntax, size too large, invalid
   * request message framing, or deceptive request routing).
   */
  ClientErrorBadRequest = 400,

  /**
   * Similar to `403 Forbidden`, but specifically for use when authentication
   * is required and has failed or has not yet been provided. The response
   * must include a WWW-Authenticate header field containing a challenge
   * applicable to the requested resource. `401` semantically means
   * "unauthenticated", i.e. the user does not have the necessary credentials.
   */
  ClientErrorUnauthorized = 401,

  /**
   * The request was valid, but the server is refusing action. The user might
   * not have the necessary permissions for a resource, or may need an account
   * of some sort.
   */
  ClientErrorForbidden = 403,

  /**
   * The requested resource could not be found but may be available in the
   * future. Subsequent requests by the client are permissible.
   */
  ClientErrorNotFound = 404,

  /**
   * Indicates that the request could not be processed because of conflict in
   * the request, such as an edit conflict between multiple simultaneous
   * updates.
   */
  ClientErrorConflict = 409,

  /**
   * A generic error message, given when an unexpected condition was
   * encountered and no more specific message is suitable.
   */
  ServerErrorInternal = 500,
}
